import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import { functions, auth } from '../firebase/config'
import { useWalletStore } from '@/store'
import bs58 from 'bs58'

// Types
interface AuthNonceResponse {
  nonce: string
  message: string
  expiresAt: number
}

interface AuthVerifyRequest {
  publicKey: string
  signature: string
  nonce: string
}

interface AuthVerifyResponse {
  token: string
  expiresAt: number
  isNewUser: boolean
}

// Firebase function references
const getAuthNonce = httpsCallable<{ publicKey: string }, AuthNonceResponse>(
  functions,
  'getAuthNonce'
)

const verifySignature = httpsCallable<AuthVerifyRequest, AuthVerifyResponse>(
  functions,
  'verifySignature'
)

/**
 * Authentication Service
 * Handles wallet-based authentication flow
 */
export class AuthService {
  private static instance: AuthService

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /**
   * Generate the message to be signed by the wallet
   */
  generateSignMessage(nonce: string, publicKey: string): string {
    const timestamp = new Date().toISOString()
    return `Trench Quest Authentication

Sign this message to verify you own this wallet.

Wallet: ${publicKey}
Nonce: ${nonce}
Timestamp: ${timestamp}

This request will not trigger a blockchain transaction or cost any gas fees.`
  }

  /**
   * Full authentication flow
   * 1. Get nonce from server
   * 2. Sign message with wallet
   * 3. Verify signature and get Firebase token
   * 4. Sign in to Firebase
   */
  async authenticate(): Promise<{ success: boolean; isNewUser: boolean }> {
    const { publicKey, signMessage, setAuth } = useWalletStore.getState()

    if (!publicKey) {
      throw new Error('Wallet not connected')
    }

    if (!signMessage) {
      throw new Error('Wallet does not support message signing')
    }

    try {
      // Step 1: Get nonce from server
      console.log('Getting auth nonce...')
      const nonceResult = await getAuthNonce({ publicKey })
      const { nonce, message } = nonceResult.data

      // Step 2: Sign the message with wallet
      console.log('Requesting wallet signature...')
      const messageBytes = new TextEncoder().encode(message)
      const signatureBytes = await signMessage(messageBytes)
      const signature = bs58.encode(signatureBytes)

      // Step 3: Verify signature and get Firebase token
      console.log('Verifying signature...')
      const verifyResult = await verifySignature({
        publicKey,
        signature,
        nonce,
      })

      const { token, expiresAt, isNewUser } = verifyResult.data

      // Step 4: Sign in to Firebase with custom token
      console.log('Signing in to Firebase...')
      await signInWithCustomToken(auth, token)

      // Store auth state
      setAuth(token, expiresAt)

      console.log('Authentication successful!')
      return { success: true, isNewUser }
    } catch (error) {
      console.error('Authentication failed:', error)
      throw error
    }
  }

  /**
   * Sign out from Firebase and clear auth state
   */
  async signOut(): Promise<void> {
    const { clearAuth } = useWalletStore.getState()

    try {
      await signOut(auth)
      clearAuth()
      console.log('Signed out successfully')
    } catch (error) {
      console.error('Sign out failed:', error)
      throw error
    }
  }

  /**
   * Check if current auth token is still valid
   */
  isTokenValid(): boolean {
    const { authExpiry } = useWalletStore.getState()
    if (!authExpiry) return false
    return Date.now() < authExpiry
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser() {
    return auth.currentUser
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChanged(callback: (user: any) => void) {
    return auth.onAuthStateChanged(callback)
  }
}

// Export singleton
export const authService = AuthService.getInstance()
